import { STR_FAILURE, STR_SUCCESS } from "core/constants/strings"
import { TTableData } from "core/constants/types"
import { useAuth } from "core/context/auth"
import useToast from "core/hooks/useToast"
import DatabaseSyncAPIDataSourceImpl from "data/API/Admin/DatabaseSyncAPIDataSourceImpl"
import { DatabaseSyncRepositoryImpl } from "data/repository/Admin/DatabaseSyncRepositoryImpl"
import { set } from "date-fns"
import DatabaseSync from "domain/useCase/Admin/DatabaseSync/databaseSync"
import GetAllCollectionsName from "domain/useCase/Admin/DatabaseSync/getAllCollectionNames"
import GetSyncedDatabase from "domain/useCase/Admin/DatabaseSync/getSyncedDatabaseCollections"
import React from "react"

export default function useDatabaseSyncViewModel() {
  const [isOpen, setIsOpen] = React.useState<boolean>(false)
  const [selectedCollections, setSelectedCollections] = React.useState<any>([])
  const [currentSelectedCollection, setCurrentSelectedCollection] = React.useState<any>("")
  const [collections, setCollections] = React.useState<any>([])
  const [loading, setLoading] = React.useState<any>(false)
  const [tableData, setTableData] = React.useState<TTableData>({
    header: ["Collection Name", "Last Synced"],
  } as TTableData)

  const { auth } = useAuth()
  const { toast, changeToastVisibility, changeToastDetails } = useToast()

  const getAllCollectionsUseCase = new GetAllCollectionsName(
    new DatabaseSyncRepositoryImpl(new DatabaseSyncAPIDataSourceImpl())
  )
  const databaseSyncUseCase = new DatabaseSync(new DatabaseSyncRepositoryImpl(new DatabaseSyncAPIDataSourceImpl()))
  const getSyncedDatabaseCollectionsUseCase = new GetSyncedDatabase(
    new DatabaseSyncRepositoryImpl(new DatabaseSyncAPIDataSourceImpl())
  )

  async function getAllCollections() {
    const response = await getAllCollectionsUseCase.invoke(auth)
    if (response.success) {
      setCollections(["All Collections", ...response.data])
    } else {
      setLoading(false)
      changeToastVisibility(true)
      if (response.error) {
        changeToastDetails(STR_FAILURE, response.error)
      } else {
        changeToastDetails(STR_FAILURE, response.data)
      }
    }
  }

  async function getSyncedDatabaseCollections() {
    const response = await getSyncedDatabaseCollectionsUseCase.invoke(auth)

    if (response.success) {
      const rows = response.data.map((item: any, index: number) => {
        const code = [
          <h2 className="text-[12px] font-[600]">{item?.collectionName || "-"}</h2>,
          <p className="text-left text-[14px] font-[600]">
            {item?.lastSyncTime ? new Date(item.lastSyncTime * 1000).toLocaleString() : "-"}
          </p>,
        ]
        return {
          code: code,
        }
      })
      setTableData((data: TTableData) => ({
        ...data,
        rows,
      }))
    } else {
      setLoading(false)
      changeToastVisibility(true)
      if (response.error) {
        changeToastDetails(STR_FAILURE, response.error)
      } else {
        changeToastDetails(STR_FAILURE, response.data)
      }
    }
  }

  function handleSelectCollection(e: any) {
    const value = e.target.value
    if (selectedCollections.includes(value)) {
      setSelectedCollections((prev: any) => prev.filter((item: any) => item !== value))
      setCurrentSelectedCollection(value)
    } else {
      if (value === "All Collections") {
        setSelectedCollections(["All Collections"])
        setCurrentSelectedCollection(value)
        changeToastDetails(STR_SUCCESS, "This will select all collections")
        changeToastVisibility(true)
      } else if (!selectedCollections.includes("All Collections")) {
        setSelectedCollections((prev: any) => [...prev, value])
        setCurrentSelectedCollection(value)
      }
    }
  }

  async function handleSyncDatabase() {
    if (selectedCollections.includes("All Collections")) {
      setSelectedCollections([])
    }
    setLoading(true)
    const response = await databaseSyncUseCase.invoke(auth, { backupDatabases: selectedCollections })
    setLoading(false)
    if (response.success) {
      changeToastVisibility(true)
      changeToastDetails(STR_SUCCESS, STR_SUCCESS)
    } else {
      changeToastVisibility(true)
      changeToastDetails(STR_FAILURE, response.error)
    }
    getSyncedDatabaseCollections()
  }

  return {
    toast,
    loading,
    isOpen,
    setIsOpen,
    tableData,
    selectedCollections,
    collections,
    currentSelectedCollection,
    setSelectedCollections,
    setCurrentSelectedCollection,
    getAllCollections,
    getSyncedDatabaseCollections,
    handleSyncDatabase,
    handleSelectCollection,
    changeToastDetails,
    changeToastVisibility,
  }
}
